import { Autocomplete, Grid, TextField } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import SpecialDivider from "../SpecialDivider";
import { Controller } from "react-hook-form";
import { AxiosResponse } from "axios";
import { useState, useEffect, useMemo } from "react";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";

interface AddressProps {
  register: any;
  index: number;
  control: any;
  getValues: any;
  title: string;
  isArray?: boolean;
  country?: any;
  county?: any;
  city?: any;
}

interface CountriesType {
  name: string;
  iso2: string;
  iso3: string;
  states: [];
}
interface CitiesType {
  name: string;
  state_code: string;
}

export default function Address(props: AddressProps) {
  const {
    register,
    index,
    control,
    getValues,
    title,
    isArray,
    country,
    county,
    city,
  } = props;
  const [countries, setCountries] = useState<CountriesType[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [counties, setCounties] = useState<CitiesType[]>([]);

  useEffect(() => {
    getInitialDataAddress();
  }, []);

  const getInitialDataAddress = async () => {
    // Get Initial Cities
    getData(`${endpoints.client.addressCheckEndpointState}`).then(
      (res: AxiosResponse) => {
        // Remove duplicate countries by name
        const seen = new Set();
        const uniqueCountries = res?.data?.data
          .filter((country: any) => {
            if (seen.has(country?.name)) return false;
            seen.add(country?.name);
            return true;
          })
          .map((country: any) => ({
            name: country?.name,
            iso2: country?.iso2,
            iso3: country?.iso3,
            states: country?.states,
          }));
        setCountries(uniqueCountries);
      }
    );
  };

  const getCityOptions = async (value: string) => {
    // Get City Based on County
    if (value.length > 3) {
      await postData(`${endpoints.client.addressCheckEndpointStateCity}`, {
        country: isArray
          ? getValues(`locations[${index}].address.country`)
          : getValues(`address.country`),
        state: value,
      }).then((res: AxiosResponse) => {
        if (res && res?.data) {
          const seen = new Set();
          const uniqueCities = res?.data?.data
            .filter((city: any) => {
              if (seen.has(city)) return false;
              seen.add(city);
              return true;
            })
            .map((city: any) => {
              return {
                name: city,
              };
            });
          setCities(uniqueCities);
        }
      });
    }
  };

  const getCountryStateOptions = (country: string) => {
    if (!country || country === "undefined") return;

    try {
      const result = countries.find((c: any) => c?.name === country);
      if (!result) return;
      setCounties(result?.states);
    } catch (error) {
      console.error("Error fetching country states:", error);
      setCounties([]);
    }
  };

  const selectedCountry = useMemo(
    () =>
      countries.find(
        (c) =>
          c.name ===
          (isArray
            ? getValues(`locations[${index}].address.country`)
            : getValues(`address.country`))
      ),
    [isArray, index, getValues, countries]
  );

  const selectedCounty = useMemo(
    () =>
      counties.find(
        (c) =>
          c?.name ===
          (isArray
            ? getValues(`locations[${index}].address.county`)
            : getValues(`address.county`))
      ),
    [isArray, index, getValues, counties]
  );

  const selectedCity = useMemo(
    () =>
      cities.find(
        (c) =>
          c.name ===
          (isArray
            ? getValues(`locations[${index}].address.city`)
            : getValues(`address.city`))
      ),
    [isArray, index, getValues, cities]
  );

  return (
    <>
      {/* ///////////////////////////////////////////////////////////////////////////ADRESA */}
      <Grid item xs={12} sm={12} md={12}>
        <SpecialDivider title={title} />
      </Grid>

      {/* Country */}
      <Grid item xs={12} sm={12} md={3}>
        <Controller
          {...(isArray
            ? { ...register(`locations[${index}].address.country`) }
            : { ...register(`address.country`) })}
          name={
            isArray ? `locations[${index}].address.country` : `.address.country`
          }
          control={control}
          defaultValue={undefined}
          render={({ field: { ref, onChange }, fieldState: { error } }) => (
            <Autocomplete
              id="country"
              freeSolo
              noOptionsText={Vocabulary.noResultsFound}
              options={countries || []}
              value={selectedCountry || country || null}
              onInputChange={(_, newValue) => {
                onChange(newValue);
                getCountryStateOptions(newValue);
              }}
              isOptionEqualToValue={(option, value) =>
                option?.name === value?.name
              }
              getOptionLabel={(option: any) => option?.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  label={Vocabulary.country}
                  fullWidth
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          )}
        />
      </Grid>

      {/* County */}
      <Grid item xs={12} sm={12} md={3}>
        <Controller
          {...(isArray
            ? { ...register(`locations[${index}].address.county`) }
            : { ...register(`address.county`) })}
          name={
            isArray ? `locations[${index}].address.county` : `address.county`
          }
          control={control}
          defaultValue={undefined}
          render={({ field: { ref, onChange }, fieldState: { error } }) => (
            <Autocomplete
              id="county"
              freeSolo
              noOptionsText={Vocabulary.noResultsFound}
              options={counties || []}
              value={selectedCounty || county || null}
              onInputChange={(_, newValue) => {
                onChange(newValue);
                getCityOptions(`${newValue} County`);
              }}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option: any) => {
                if ((option?.name as string).includes("County")) {
                  return (option?.name as string).split(" ")[0];
                }
                return option?.name;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  label={Vocabulary.county}
                  fullWidth
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          )}
        />
      </Grid>

      {/* City */}
      <Grid item xs={12} sm={12} md={6}>
        <Controller
          {...(isArray
            ? { ...register(`locations[${index}].address.city`) }
            : { ...register(`address.city`) })}
          name={isArray ? `locations[${index}].address.city` : `address.city`}
          control={control}
          defaultValue={undefined}
          render={({ field: { ref, onChange }, fieldState: { error } }) => (
            <Autocomplete
              id="city"
              freeSolo
              noOptionsText={Vocabulary.noResultsFound}
              options={cities || []}
              value={selectedCity || city || null}
              onInputChange={(_, newValue) => {
                onChange(newValue);
              }}
              isOptionEqualToValue={(option, value) =>
                option?.name === value?.name
              }
              getOptionLabel={(option: any) => option?.name || option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  label={Vocabulary.city}
                  fullWidth
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <TextField
          {...(isArray
            ? { ...register(`locations[${index}].address.postcode`) }
            : { ...register(`address.postcode`) })}
          name={
            isArray
              ? `locations[${index}].address.postcode`
              : `address.postcode`
          }
          id="postcode"
          label={Vocabulary.postcode}
          fullWidth
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={8}>
        <TextField
          {...(isArray
            ? { ...register(`locations[${index}].address.details`) }
            : { ...register(`address.details`) })}
          name={
            isArray ? `locations[${index}].address.details` : `address.details`
          }
          id="details"
          label={Vocabulary.addressDetails}
          fullWidth
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
    </>
  );
}
